/**
 * ProfilePage — two-tab settings (light theme).
 *   /app/profile  → "<Programme> Profile"
 *   /app/settings → "Account Settings"
 */

import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loader2, Plus, X, Save, Lock, Trash2, AlertTriangle, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import api from '../../lib/api'
import { programmeLabel } from '../../utils/programme'
import { useAuthStore } from '../../store/authStore'

const SURFACE = '#FFFFFF'
const BG      = '#F4F7FC'
const BORDER  = '#DDE4EF'
const TEXT    = '#0C1629'
const MUTED   = '#506280'
const FAINT   = '#8EA4BC'
const GOLD    = '#2563eb'

// ─── Shared field wrapper ─────────────────────────────────────────────────────

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block font-sans text-xs font-medium mb-1.5" style={{ color: MUTED }}>
        {label}{required && <span style={{ color: GOLD }}> *</span>}
      </label>
      {hint && <p className="font-sans text-xs mb-2" style={{ color: FAINT }}>{hint}</p>}
      {children}
    </div>
  )
}

const inputCls = "w-full px-3.5 py-2.5 rounded-lg font-sans text-sm placeholder:text-[#8EA4BC] outline-none transition-all"
const inputSty = { background: BG, border: `1px solid ${BORDER}`, color: TEXT }
const focusHandlers = {
  onFocus: e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.45)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.10)' },
  onBlur:  e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.boxShadow = 'none' },
}

// ─── Keyword tag input ────────────────────────────────────────────────────────

function KeywordInput({ keywords, onChange }) {
  const [val, setVal] = useState('')

  const add = raw => {
    const kw = raw.trim().replace(/,$/, '')
    if (!kw || keywords.includes(kw)) return
    onChange([...keywords, kw]); setVal('')
  }

  return (
    <div className="rounded-lg px-3 py-2 min-h-[44px] flex flex-wrap gap-1.5 cursor-text transition-all"
      style={{ ...inputSty }}
      onFocus={e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.45)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.10)' }}
      onBlur={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.boxShadow = 'none' }}
      tabIndex={-1}>
      {keywords.map(kw => (
        <span key={kw} className="flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-xs"
          style={{ background: 'rgba(37,99,235,0.08)', color: '#1d4ed8', border: '1px solid rgba(37,99,235,0.25)' }}>
          {kw}
          <button type="button" onClick={() => onChange(keywords.filter(k => k !== kw))}
            className="opacity-60 hover:opacity-100 transition-opacity">
            <X style={{ width: 10, height: 10 }} />
          </button>
        </span>
      ))}
      <input value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(val) } }}
        onBlur={() => { if (val.trim()) add(val) }}
        placeholder={keywords.length ? '' : 'Type keyword, press Enter'}
        className="bg-transparent outline-none font-mono text-xs flex-1 min-w-24"
        style={{ color: TEXT }} />
    </div>
  )
}

// ─── Section card wrapper ─────────────────────────────────────────────────────

function Section({ icon, title, children, danger }) {
  return (
    <div className="rounded-2xl p-6 space-y-4 shadow-card"
      style={{ background: SURFACE, border: `1px solid ${danger ? 'rgba(220,38,38,0.25)' : BORDER}` }}>
      {(icon || title) && (
        <div className="flex items-center gap-2 pb-1" style={{ borderBottom: `1px solid ${BORDER}` }}>
          {icon && <span style={{ color: danger ? '#DC2626' : GOLD }}>{icon}</span>}
          <h2 className="font-sans text-sm font-semibold" style={{ color: danger ? '#DC2626' : TEXT }}>{title}</h2>
        </div>
      )}
      {children}
    </div>
  )
}

// ─── Constants ────────────────────────────────────────────────────────────────

const UNIVERSITIES = ['Kennedy University', 'Dunster Business School', 'London School of Business and Science (LSBS)', 'Woxsen University', 'Other']
const PROGRAMMES   = ['PhD', 'DBA', 'Honorary Doctorate']
const DOMAINS      = ['Management', 'Business', 'Social Sciences', 'Technology', 'Environmental Policy', 'Healthcare', 'Education', 'Finance', 'Law', 'Other']
const YEARS        = Array.from({ length: 9 }, (_, i) => new Date().getFullYear() + i)

// ─── Programme profile tab ──────────────────────────────────────────────────────────

function ProfileTab({ profile }) {
  const { updateProfile } = useAuthStore()
  const [form, setForm] = useState({
    fullName:         profile?.full_name         || '',
    university:       profile?.university         || '',
    // No silent default — defaulting to a degree the user never picked is how
    // a DBA profile ends up reading "PhD" everywhere.
    programme:        profile?.programme          || '',
    supervisorName:   profile?.supervisor_name    || '',
    enrollmentNumber: profile?.enrollment_number  || '',
    submissionYear:   profile?.submission_year    || '',
    researchTopic:    profile?.research_topic     || '',
    domain:           profile?.domain             || '',
    keywords:         profile?.keywords           || [],
    rawReferences:    profile?.raw_references     || '',
  })
  const [saving, setSaving] = useState(false)
  const [saved,  setSaved]  = useState(false)
  const [error,  setError]  = useState('')

  const set = k => e => setForm(prev => ({ ...prev, [k]: e.target.value }))

  const save = async () => {
    setSaving(true); setError('')
    try {
      const payload = {
        fullName:         form.fullName,
        university:       form.university,
        programme:        form.programme,
        supervisorName:   form.supervisorName,
        enrollmentNumber: form.enrollmentNumber,
        submissionYear:   form.submissionYear,
        researchTopic:    form.researchTopic,
        domain:           form.domain,
        keywords:         form.keywords,
        rawReferences:    form.rawReferences,
      }

      let savedProfile = null
      try {
        // Goes through the backend's service-role client — the profiles
        // table's RLS only grants admins direct access, so a browser-side
        // write here silently no-ops (no error, no row change) rather than
        // throwing, which is why edits used to look saved but never stuck.
        const { data } = await api.put('/profile', payload)
        if (!data?.profile) throw new Error('invalid')
        savedProfile = data.profile
      } catch {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')
        const dbPayload = {
          id:                 user.id,
          full_name:          payload.fullName,
          university:         payload.university,
          programme:          payload.programme,
          supervisor_name:    payload.supervisorName,
          enrollment_number:  payload.enrollmentNumber,
          submission_year:    payload.submissionYear ? parseInt(payload.submissionYear) : null,
          research_topic:     payload.researchTopic,
          domain:             payload.domain,
          keywords:           payload.keywords,
          raw_references:     payload.rawReferences,
          updated_at:         new Date().toISOString(),
        }
        const { data: row, error: err } = await supabase.from('profiles').upsert(dbPayload).select().single()
        if (err) throw err
        savedProfile = row
      }

      updateProfile(savedProfile)
      setSaved(true); setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      setError(err.message || 'Failed to save')
    } finally { setSaving(false) }
  }

  const selectCls = `${inputCls} cursor-pointer`

  return (
    <div className="space-y-5 max-w-2xl">
      <Section title="Personal Details">
        <Field label="Full Name" required>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            value={form.fullName} onChange={set('fullName')} placeholder="Dr. / Mr. / Ms." />
        </Field>
      </Section>

      <Section title="Programme Details">
        <div className="grid grid-cols-2 gap-4">
          <Field label="University" required>
            <select className={selectCls} style={{ ...inputSty, appearance: 'none' }} value={form.university} onChange={set('university')}>
              <option value="">Select university</option>
              {UNIVERSITIES.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Programme" required>
            <select className={selectCls} style={{ ...inputSty, appearance: 'none' }} value={form.programme} onChange={set('programme')}>
              <option value="">Select programme</option>
              {PROGRAMMES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Supervisor Name">
            <input className={inputCls} style={inputSty} {...focusHandlers}
              value={form.supervisorName} onChange={set('supervisorName')} placeholder="Prof. …" />
          </Field>
          <Field label="Enrollment Number">
            <input className={inputCls} style={inputSty} {...focusHandlers}
              value={form.enrollmentNumber} onChange={set('enrollmentNumber')} placeholder="e.g. PHD2023001" />
          </Field>
          <Field label="Expected Submission Year">
            <select className={selectCls} style={{ ...inputSty, appearance: 'none' }} value={form.submissionYear} onChange={set('submissionYear')}>
              <option value="">Select year</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Research Profile">
        <Field label="Research Topic" required hint={`Describe your ${programmeLabel(form.programme)} research area in 1–3 sentences.`}>
          <textarea className={inputCls} style={inputSty} {...focusHandlers} rows={3}
            value={form.researchTopic} onChange={set('researchTopic')}
            placeholder="e.g. The impact of AI on managerial decision-making in SMEs across South Asia" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Domain / Discipline">
            <select className={selectCls} style={{ ...inputSty, appearance: 'none' }} value={form.domain} onChange={set('domain')}>
              <option value="">Select domain</option>
              {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Keywords" hint="Used by AI to personalise suggestions. Press Enter or comma to add.">
          <KeywordInput keywords={form.keywords} onChange={kws => setForm(prev => ({ ...prev, keywords: kws }))} />
        </Field>
        <Field label="Reference Notes" hint="Paste your key references here — used as context for AI generation.">
          <textarea className={inputCls} style={inputSty} {...focusHandlers} rows={4}
            value={form.rawReferences} onChange={set('rawReferences')}
            placeholder="Paste your references list (any format)…" />
        </Field>
      </Section>

      {error && (
        <p className="font-sans text-sm" style={{ color: '#DC2626' }}>{error}</p>
      )}

      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 px-6 py-3 rounded-xl font-sans text-sm font-semibold transition-all disabled:opacity-50"
        style={{ background: saved ? '#16A34A' : GOLD, color: '#FFFFFF' }}
        onMouseEnter={e => { if (!saving) e.currentTarget.style.background = saved ? '#15803D' : '#3b82f6' }}
        onMouseLeave={e => e.currentTarget.style.background = saved ? '#16A34A' : GOLD}>
        {saving ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : saved ? <Check style={{ width: 15, height: 15 }} /> : <Save style={{ width: 15, height: 15 }} />}
        {saved ? 'Saved!' : 'Update Profile'}
      </button>
    </div>
  )
}

// ─── Account Settings tab ─────────────────────────────────────────────────────

function AccountSettingsTab({ user }) {
  const { logout } = useAuthStore()
  const navigate   = useNavigate()

  const [pwForm,    setPwForm]    = useState({ newPw: '', confirm: '' })
  const [pwSaving,  setPwSaving]  = useState(false)
  const [pwError,   setPwError]   = useState('')
  const [pwSuccess, setPwSuccess] = useState(false)

  const [delConfirm, setDelConfirm] = useState(false)
  const [delInput,   setDelInput]   = useState('')
  const [deleting,   setDeleting]   = useState(false)

  const changePassword = async () => {
    setPwError(''); setPwSuccess(false)
    if (pwForm.newPw.length < 6) { setPwError('Password must be at least 6 characters'); return }
    if (pwForm.newPw !== pwForm.confirm) { setPwError('Passwords do not match'); return }
    setPwSaving(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: pwForm.newPw })
      if (error) throw error
      setPwForm({ newPw: '', confirm: '' })
      setPwSuccess(true); setTimeout(() => setPwSuccess(false), 3000)
    } catch (err) {
      setPwError(err.message || 'Failed to update password')
    } finally { setPwSaving(false) }
  }

  const deleteAccount = async () => {
    if (delInput !== 'DELETE') return
    setDeleting(true)
    try {
      await api.delete('/auth/account')
      await logout()
      navigate('/login', { replace: true })
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete account')
      setDeleting(false)
    }
  }

  const setPw = k => e => setPwForm(prev => ({ ...prev, [k]: e.target.value }))

  return (
    <div className="space-y-5 max-w-2xl">
      <Section title="Identity">
        <Field label="Email" hint="Email is managed by Supabase Auth and cannot be changed here.">
          <input className={inputCls} style={{ ...inputSty, opacity: 0.6, cursor: 'not-allowed' }}
            value={user?.email || ''} readOnly />
        </Field>
        <Field label="User ID">
          <input className={inputCls}
            style={{ ...inputSty, opacity: 0.5, cursor: 'not-allowed', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
            value={user?.id || ''} readOnly />
        </Field>
      </Section>

      <Section icon={<Lock style={{ width: 13, height: 13 }} />} title="Change Password">
        <Field label="New Password">
          <input type="password" className={inputCls} style={inputSty} {...focusHandlers}
            value={pwForm.newPw} onChange={setPw('newPw')} placeholder="At least 6 characters" />
        </Field>
        <Field label="Confirm New Password">
          <input type="password" className={inputCls} style={inputSty} {...focusHandlers}
            value={pwForm.confirm} onChange={setPw('confirm')} placeholder="Repeat new password" />
        </Field>
        {pwError && (
          <p className="font-sans text-xs" style={{ color: '#DC2626' }}>{pwError}</p>
        )}
        {pwSuccess && (
          <p className="font-sans text-xs font-medium" style={{ color: '#16A34A' }}>Password updated successfully.</p>
        )}
        <button onClick={changePassword} disabled={!pwForm.newPw || !pwForm.confirm || pwSaving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-sans text-sm font-medium transition-all disabled:opacity-50"
          style={{ background: GOLD, color: '#FFFFFF' }}
          onMouseEnter={e => { if (!pwSaving) e.currentTarget.style.background = '#3b82f6' }}
          onMouseLeave={e => e.currentTarget.style.background = GOLD}>
          {pwSaving ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Lock style={{ width: 14, height: 14 }} />}
          Update Password
        </button>
      </Section>

      <Section danger icon={<AlertTriangle style={{ width: 13, height: 13 }} />} title="Danger Zone">
        {!delConfirm ? (
          <>
            <p className="font-sans text-xs" style={{ color: MUTED }}>
              Permanently delete your account and all associated data — theses, documents, references, and chats. This cannot be undone.
            </p>
            <button onClick={() => setDelConfirm(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
              style={{ border: '1px solid rgba(220,38,38,0.35)', color: '#DC2626', background: 'rgba(220,38,38,0.04)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(220,38,38,0.04)'}>
              <Trash2 style={{ width: 14, height: 14 }} />Delete Account
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <p className="font-sans text-xs font-medium" style={{ color: '#DC2626' }}>
              Type <span className="font-mono font-bold">DELETE</span> to confirm permanent account deletion.
            </p>
            <input className={inputCls}
              style={{ ...inputSty, borderColor: 'rgba(220,38,38,0.4)' }}
              value={delInput} onChange={e => setDelInput(e.target.value)} placeholder="DELETE" />
            <div className="flex gap-2">
              <button onClick={() => { setDelConfirm(false); setDelInput('') }}
                className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
                style={{ border: `1px solid ${BORDER}`, color: MUTED }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Cancel
              </button>
              <button onClick={deleteAccount} disabled={delInput !== 'DELETE' || deleting}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium disabled:opacity-40 transition-all"
                style={{ background: '#DC2626', color: '#FFFFFF' }}>
                {deleting ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Trash2 style={{ width: 14, height: 14 }} />}
                Delete My Account
              </button>
            </div>
          </div>
        )}
      </Section>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const location = useLocation()
  const { user, profile } = useAuthStore()

  const defaultTab = location.pathname.includes('settings') ? 'account' : 'profile'
  const [tab, setTab] = useState(defaultTab)

  const TABS = [
    { id: 'profile', label: `${programmeLabel(profile?.programme)} Profile` },
    { id: 'account', label: 'Account Settings' },
  ]

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-xl font-semibold" style={{ color: TEXT }}>Settings</h1>
        <p className="font-sans text-sm mt-1" style={{ color: FAINT }}>Manage your {programmeLabel(profile?.programme)} profile and account preferences.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-8 p-1 rounded-xl w-fit shadow-card"
        style={{ background: SURFACE, border: `1px solid ${BORDER}` }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="px-5 py-2 rounded-lg font-sans text-sm font-medium transition-all"
            style={{
              background: tab === t.id ? BG : 'transparent',
              color:      tab === t.id ? TEXT : FAINT,
              border:     tab === t.id ? `1px solid ${BORDER}` : '1px solid transparent',
              boxShadow:  tab === t.id ? '0 1px 3px rgba(12,22,41,0.06)' : 'none',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && <ProfileTab profile={profile} />}
      {tab === 'account' && <AccountSettingsTab user={user} />}
    </div>
  )
}
